import { getAccessToken, getCurrentUser } from "./AuthService";

const API_BASE_URL = "https://paiservices.azurewebsites.net/api";

// Cache for the current account (includes EntityId)
let cachedAccount = null;

// Helper function to make authenticated API calls
const authenticatedFetch = async (url, options = {}) => {
  const accessToken = await getAccessToken();
  
  const headers = {
    ...options.headers,
    "Authorization": `Bearer ${accessToken}`,
    "Content-Type": "application/json",
  };
  
  const response = await fetch(url, {
    ...options,
    headers,
  });
  
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`API Error (${response.status}): ${errorText}`);
  }
  
  return response;
};

// Account Service - manages user account and EntityId
export const AccountService = {
  // Get or create account - returns the account with EntityId
  getOrCreateAccount: async () => {
    // Return cached account if available
    if (cachedAccount) {
      // console.log('Using cached account:', cachedAccount);
      return cachedAccount;
    }
    
    const user = getCurrentUser();
    if (!user) {
      throw new Error('No active account. Please login first.');
    }
    
    const response = await authenticatedFetch(`${API_BASE_URL}/accounts`, {
      method: 'POST',
      body: JSON.stringify({
        name: user.name || user.email?.split('@')[0] || 'User',
        email: user.email || '',
        provider: 'AzureAD',
      }),
    });
    
    const accountData = await response.json();
    
    // Helper to get property (handles both PascalCase and camelCase from API)
    const getProp = (obj, ...keys) => {
      for (const key of keys) {
        if (obj[key] !== undefined) return obj[key];
      }
      return undefined;
    };
    
    // Cache the account data - handle both PascalCase and camelCase API responses
    cachedAccount = {
      id: getProp(accountData, 'Id', 'id'),
      entityId: getProp(accountData, 'EntityId', 'entityId'),
      email: getProp(accountData, 'Email', 'email'),
      name: getProp(accountData, 'Name', 'name'),
      organizationId: getProp(accountData, 'OrganizationId', 'organizationId'),
      personas: getProp(accountData, 'Personas', 'personas') || [],
      defaultPersona: getProp(accountData, 'DefaultPersona', 'defaultPersona'),
    };
    
    // console.log('Account fetched and cached:', cachedAccount);
    // console.log('EntityId being used for API calls:', cachedAccount.entityId);
    return cachedAccount;
  },
  
  // Clear the cached account (call on logout)
  clearCache: () => {
    cachedAccount = null;
  },
  
  // Get the EntityId (fetches account if not cached)
  getEntityId: async () => {
    const account = await AccountService.getOrCreateAccount();
    return account.entityId;
  },
};

// Get the current user's account ID (EntityId from backend) - async version
const getAccountIdAsync = async () => {
  const entityId = await AccountService.getEntityId();
  // console.log('Using EntityId for API calls:', entityId);
  return entityId;
};

// Dynamic Books API
export const DynamicBooksService = {
  // Get list of available books
  getBooks: async () => {
    const accountId = await getAccountIdAsync();
    const response = await authenticatedFetch(`${API_BASE_URL}/dynamicbooks?accountId=${encodeURIComponent(accountId)}`);
    return response.json();
  },
  
  // Get a specific book by ID
  getBook: async (bookId) => {
    const accountId = await getAccountIdAsync();
    const response = await authenticatedFetch(`${API_BASE_URL}/dynamicbooks/${bookId}?accountId=${encodeURIComponent(accountId)}`);
    return response.json();
  },
  
  // Get book content/chapters
  getBookContent: async (bookId) => {
    const accountId = await getAccountIdAsync();
    const response = await authenticatedFetch(`${API_BASE_URL}/dynamicbooks/${bookId}/content?accountId=${encodeURIComponent(accountId)}`);
    return response.json();
  },
  
  // Create a new book (just creates DB record, doesn't orchestrate)
  createBook: async (bookData) => {
    const accountId = await getAccountIdAsync();
    const response = await authenticatedFetch(`${API_BASE_URL}/dynamicbooks`, {
      method: "POST",
      body: JSON.stringify({
        accountId: accountId,
        bookName: bookData.bookName,
        bookAuthor: bookData.bookAuthor,
        bookPersona: bookData.bookPersona,
        infographicUrl: bookData.infographicUrl,
        storageFolder: bookData.storageFolder,
      }),
    });
    return response.json();
  },

  // Update an existing book with additional data (e.g., token usage)
  updateBook: async (bookId, updateData) => {
    const accountId = await getAccountIdAsync();
    const response = await authenticatedFetch(`${API_BASE_URL}/dynamicbooks/${bookId}`, {
      method: "PUT",
      body: JSON.stringify({
        accountId: accountId,
        ...updateData,
      }),
    });
    return response.json();
  },

  // Orchestrate and build a new book (calls AI agents - takes 1-2 minutes)
  orchestrateBook: async (bookData) => {
    const accountId = await getAccountIdAsync();
    
    // console.log('Build book request:', JSON.stringify(bookData, null, 2));
    
    const response = await authenticatedFetch(`${API_BASE_URL}/dynamicbooks/build`, {
      method: "POST",
      body: JSON.stringify({
        accountId: accountId,
        bookTitle: bookData.bookName,
        bookAuthor: bookData.bookAuthor,
        persona: bookData.bookPersona,
      }),
    });

    return response.json();
  },
  
  // Delete a book
  deleteBook: async (bookId) => {
    const accountId = await getAccountIdAsync();
    const response = await authenticatedFetch(`${API_BASE_URL}/dynamicbooks/${bookId}?accountId=${encodeURIComponent(accountId)}`, {
      method: "DELETE",
    });
    return response.ok;
  },
};

// User Profile API
export const UserService = {
  // Get current user profile
  getProfile: async () => {
    const response = await authenticatedFetch(`${API_BASE_URL}/user/profile`);
    return response.json();
  },
  
  // Update user profile
  updateProfile: async (profileData) => {
    const response = await authenticatedFetch(`${API_BASE_URL}/user/profile`, {
      method: "PUT",
      body: JSON.stringify(profileData),
    });
    return response.json();
  },
  
  // Get user's book library
  getLibrary: async () => {
    const response = await authenticatedFetch(`${API_BASE_URL}/user/library`);
    return response.json();
  },
  
  // Add book to user's library
  addToLibrary: async (bookId) => {
    const response = await authenticatedFetch(`${API_BASE_URL}/user/library`, {
      method: "POST",
      body: JSON.stringify({ bookId }),
    });
    return response.json();
  },
};

// Subscription/Stripe API
export const SubscriptionService = {
  // Get current subscription status
  getSubscription: async () => {
    const response = await authenticatedFetch(`${API_BASE_URL}/subscription`);
    return response.json();
  },
  
  // Create checkout session
  createCheckoutSession: async (priceId, successUrl, cancelUrl) => {
    const response = await authenticatedFetch(`${API_BASE_URL}/subscription/checkout`, {
      method: "POST",
      body: JSON.stringify({ priceId, successUrl, cancelUrl }),
    });
    return response.json();
  },
  
  // Create customer portal session
  createPortalSession: async (returnUrl) => {
    const response = await authenticatedFetch(`${API_BASE_URL}/subscription/portal`, {
      method: "POST",
      body: JSON.stringify({ returnUrl }),
    });
    return response.json();
  },
  
  // Cancel subscription
  cancelSubscription: async () => {
    const response = await authenticatedFetch(`${API_BASE_URL}/subscription/cancel`, {
      method: "POST",
    });
    return response.json();
  },
};

// Progress Tracking API
export const ProgressService = {
  // Get user's progress on a book
  getBookProgress: async (bookId) => {
    const response = await authenticatedFetch(`${API_BASE_URL}/progress/${bookId}`);
    return response.json();
  },
  
  // Update progress on a chapter
  updateChapterProgress: async (bookId, chapterId, progressData) => {
    const response = await authenticatedFetch(`${API_BASE_URL}/progress/${bookId}/${chapterId}`, {
      method: "PUT",
      body: JSON.stringify(progressData),
    });
    return response.json();
  },
  
  // Mark quiz as completed
  completeQuiz: async (bookId, quizId, score) => {
    const response = await authenticatedFetch(`${API_BASE_URL}/progress/${bookId}/quiz/${quizId}`, {
      method: "POST",
      body: JSON.stringify({ score }),
    });
    return response.json();
  },
};

export default {
  DynamicBooksService,
  UserService,
  SubscriptionService,
  ProgressService,
};
